import type { IconName } from '@glplus/glpweb-dsl';

/**
 * The single source of truth for the admin portal's tool catalog. The header
 * top-nav, the left "More tools" rail, and the dashboard cards all derive from
 * this list so they never drift out of sync.
 *
 * `status` reflects what's actually shipped today (NOT the original Figma,
 * which predated Recipe moderation / Recall console / User management going
 * live). A `to` is present iff the tool has a real route; `status: 'soon'`
 * tools are roadmap stubs with no route yet.
 */
export type ToolStatus = 'live' | 'soon';
export type SidebarSection = 'catalog' | 'support' | 'delivery' | 'operations';

export interface AdminTool {
  key: string;
  /** Card title (and sidebar label, unless `sidebarLabel` overrides). */
  label: string;
  /** Shorter label for the header top-nav (when `inTopNav`). */
  navLabel?: string;
  /** Shorter label for the left rail (falls back to `label`). */
  sidebarLabel?: string;
  /** One-line description for the dashboard card. */
  description: string;
  /** Route path — present only for live tools. */
  to?: string;
  /** Capability token shown on the card + gating the live dashboard card. */
  capability: string;
  icon: IconName;
  status: ToolStatus;
  /** Appears in the header top-nav (the primary, always-on tools). */
  inTopNav?: boolean;
  /** Section of the left "More tools" rail this belongs to. */
  sidebarSection?: SidebarSection;
  /** Show as a dashboard card. Defaults true; set false for sidebar-only tools. */
  onDashboard?: boolean;
}

export const ADMIN_TOOLS: AdminTool[] = [
  { key: 'videos', label: 'Feature and tutorial videos', description: 'Edit videos, captions and chapters; publish without an app build.',
    to: '/videos', capability: 'video:read', icon: 'listAlt', status: 'live', sidebarSection: 'support' },
  // ── Primary live tools (header top-nav + dashboard "Your tools") ──
  {
    key: 'missing-upc',
    label: 'Missing UPC queue',
    navLabel: 'Missing UPCs',
    description: 'Resolve products missing a barcode with a real UPC, size, and image.',
    to: '/missing-upc',
    capability: 'missingupc:read',
    icon: 'listAlt',
    status: 'live',
    inTopNav: true,
  },
  {
    key: 'missing-manufacturers',
    label: 'Missing manufacturer data',
    navLabel: 'Manufacturers',
    description:
      'Which manufacturers still need a product-data source identified (size discovery).',
    to: '/missing-manufacturers',
    capability: 'product:read',
    icon: 'listAlt',
    status: 'live',
    inTopNav: false,
  },
  {
    key: 'product-images',
    label: 'Product images',
    navLabel: 'Product Images',
    description:
      'Source photos for products that have none. Paste an image URL; we host our own copy.',
    to: '/product-images',
    capability: 'productimage:read',
    icon: 'listAlt',
    status: 'live',
    inTopNav: true,
  },
  {
    key: 'upc-review',
    label: 'UPC review queue',
    navLabel: 'UPC Review',
    description: 'Decide ambiguous barcode corrections from probe evidence; apply confirmed fixes in batch.',
    to: '/upc-review',
    capability: 'upcreview:read',
    icon: 'listAlt',
    status: 'live',
    inTopNav: false,
  },
  {
    key: 'store-requests',
    label: 'Store requests',
    navLabel: 'Store Requests',
    description: 'See which stores users most want added, and triage each request to fulfilled.',
    to: '/store-requests',
    capability: 'storereq:read',
    icon: 'shop',
    status: 'live',
    inTopNav: true,
  },
  {
    key: 'audit-log',
    label: 'Audit log',
    navLabel: 'Audit Log',
    description: 'Review every admin action — who changed what, when, with full before/after detail.',
    to: '/audit-log',
    capability: 'audit:read',
    icon: 'list',
    status: 'live',
    inTopNav: true,
  },

  // ── Catalog tools (left rail + dashboard) ──
  {
    key: 'recipes',
    label: 'Recipe moderation',
    description: 'Publish / unpublish and fix mis-classified cuisine & meal type.',
    to: '/recipes',
    capability: 'recipe:read',
    icon: 'restaurantMenu',
    status: 'live',
    sidebarSection: 'catalog',
  },
  {
    key: 'comments',
    label: 'Comment moderation',
    description: 'Approve or reject reader comments on recipes. Nothing is visible until approved.',
    to: '/comments',
    capability: 'comment:read',
    icon: 'messages',
    status: 'live',
    sidebarSection: 'catalog',
  },
  {
    key: 'recalls',
    label: 'Recall console',
    description: 'Review active recalls; link / unlink them to catalog products.',
    to: '/recalls',
    capability: 'recall:read',
    icon: 'foodrecalls',
    status: 'live',
    sidebarSection: 'catalog',
  },
  {
    key: 'product-data-reports',
    label: 'Product data reports',
    sidebarLabel: 'Data reports',
    description: 'Review products shoppers flagged as having incorrect data, then resolve or dismiss each report.',
    to: '/product-data-reports',
    capability: 'product:read',
    icon: 'errorOutline',
    status: 'live',
    sidebarSection: 'catalog',
  },
  {
    key: 'product-editor',
    label: 'Product editor & dedup',
    sidebarLabel: 'Product editor',
    description: 'Edit canonical products and review duplicate variants.',
    to: '/product-editor',
    capability: 'product:read',
    icon: 'edit',
    status: 'live',
    sidebarSection: 'catalog',
  },

  // ── Support / content (left rail + dashboard) ──
  {
    key: 'faqs',
    label: 'FAQ management',
    description: 'Create, edit, reorder, and show/hide the public Help / FAQ entries (EN + ES).',
    to: '/faqs',
    capability: 'faq:read',
    icon: 'menuBook',
    status: 'live',
    sidebarSection: 'support',
  },
  {
    key: 'support',
    label: 'Support requests',
    description: 'Triage, answer, and resolve messages from the public contact form.',
    to: '/support',
    capability: 'support:read',
    icon: 'messages',
    status: 'live',
    sidebarSection: 'support',
  },

  // ── Delivery (left rail + dashboard) ──
  {
    key: 'relationship-reports', label: 'Runner and customer reports',
    description: 'Review private safety reports, evidence and review status.',
    to: '/relationship-reports', capability: 'support:read', icon: 'messages', status: 'live', sidebarSection: 'support',
  },
  {
    key: 'runner-applications',
    label: 'Runner applications',
    description:
      'Review Runner applicants, walk the onboarding pipeline (review → profile → insurance → handbook → video call → background check → go-live), and mint recruit codes.',
    to: '/runner-applications',
    capability: 'runner:read',
    icon: 'onlineordering',
    status: 'live',
    sidebarSection: 'delivery',
  },
  {
    key: 'runner-photos',
    label: 'Runner photos',
    description:
      'Review the face a household opens the door to — every first upload and every change, against the ID verified at onboarding.',
    to: '/runner-photos',
    capability: 'runner:read',
    icon: 'onlineordering',
    status: 'live',
    sidebarSection: 'delivery',
  },
  {
    key: 'runner-insurance',
    label: 'Runner insurance',
    description:
      'Auto insurance cards from applicants and renewals from live Runners. Without an approved, current card a Runner cannot take new work.',
    to: '/runner-insurance',
    capability: 'runner:read',
    icon: 'onlineordering',
    status: 'live',
    sidebarSection: 'delivery',
  },
  {
    key: 'runner-limits',
    label: 'Runner limits',
    description:
      'Requests to deliver to more households or more deliveries a week — the record pack, the decision, and setting a Runner\'s limits by hand.',
    to: '/runner-limits',
    capability: 'runner:read',
    icon: 'onlineordering',
    status: 'live',
    sidebarSection: 'delivery',
  },
  {
    key: 'orders-needing-cover',
    label: 'Deliveries needing a Runner',
    description:
      'The household side of a released run: who is affected, what they have been told, and whether anything was charged. Read-only — the record, not a decision.',
    to: '/orders-needing-cover',
    capability: 'user:read',
    icon: 'onlineordering',
    status: 'live',
    sidebarSection: 'delivery',
  },
  {
    key: 'order-reviews',
    label: 'Order reviews',
    description:
      'Reported problems and Runner safety exits. The charge and the Runner’s pay are held until you decide.',
    to: '/order-reviews',
    capability: 'user:read',
    icon: 'onlineordering',
    status: 'live',
    sidebarSection: 'delivery',
  },
  {
    key: 'ordering-reviews',
    label: 'Cancellation reviews',
    description:
      'Customers paused a second time within 6 months for late changes or repeated moves. Reinstate, keep paused, or remove an event we caused.',
    to: '/ordering-reviews',
    capability: 'user:read',
    icon: 'onlineordering',
    status: 'live',
    sidebarSection: 'delivery',
  },
  {
    key: 'cover-queue',
    label: 'Cover queue',
    description:
      'Deliveries a Runner released by booking time off. The run is offered automatically; a person widens the search or names a Runner when a zone comes up empty.',
    to: '/cover-queue',
    capability: 'runner:read',
    icon: 'onlineordering',
    status: 'live',
    sidebarSection: 'delivery',
  },

  // ── Delivery (left rail only) ──

  // ── Operations (left rail + dashboard) ──
  {
    key: 'jobs',
    label: 'Background-job runner',
    sidebarLabel: 'Background jobs',
    description: 'Run any of the ~99 background jobs on demand + review run history.',
    capability: 'job:read',
    icon: 'recurring',
    status: 'live',
    to: '/jobs',
    sidebarSection: 'operations',
  },
  {
    key: 'users',
    label: 'User management',
    description: 'Invite, edit, suspend, and manage end-user accounts.',
    to: '/users',
    capability: 'user:read',
    icon: 'accountInfo',
    status: 'live',
    sidebarSection: 'operations',
  },
  {
    key: 'staff',
    label: 'Staff & roles',
    description: 'Assign GL Plus staff per-app access (Consumer / Vendor / Driver / Admin).',
    to: '/staff',
    capability: 'staff:manage',
    icon: 'accountInfo',
    status: 'live',
    sidebarSection: 'operations',
  },
  {
    key: 'mailing-list',
    label: 'Mailing list',
    description: 'Marketing subscribers by channel (email / SMS / push) with counts and CSV export.',
    to: '/mailing-list',
    capability: 'mailinglist:read',
    icon: 'subscription',
    status: 'live',
    sidebarSection: 'operations',
  },
];

/** Tools shown in the header top-nav (in order). */
export const TOP_NAV_TOOLS = ADMIN_TOOLS.filter((t) => t.inTopNav);

/** Sidebar sections, in display order, with their member tools. */
export const SIDEBAR_SECTIONS: { key: SidebarSection; label: string; tools: AdminTool[] }[] = [
  { key: 'catalog', label: 'Catalog', tools: ADMIN_TOOLS.filter((t) => t.sidebarSection === 'catalog') },
  { key: 'support', label: 'Support', tools: ADMIN_TOOLS.filter((t) => t.sidebarSection === 'support') },
  { key: 'delivery', label: 'Delivery', tools: ADMIN_TOOLS.filter((t) => t.sidebarSection === 'delivery') },
  { key: 'operations', label: 'Operations', tools: ADMIN_TOOLS.filter((t) => t.sidebarSection === 'operations') },
];

/** Live tools that render as dashboard cards under "Your tools". */
export const DASHBOARD_LIVE_TOOLS = ADMIN_TOOLS.filter(
  (t) => t.status === 'live' && t.onDashboard !== false,
);

/** Roadmap tools that render as greyed "Soon" dashboard cards. */
export const DASHBOARD_ROADMAP_TOOLS = ADMIN_TOOLS.filter(
  (t) => t.status === 'soon' && t.onDashboard !== false,
);
